import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { playClickSound } from '@/utils/soundUtils';
import { useTranslation } from 'react-i18next';

interface AuthHeaderProps {
  className?: string;
  children?: React.ReactNode;
  title?: string;
  subtitle?: string;
}

/**
 * Header component for authentication pages (User Login, Activate, Forgot Password, Reset Password)
 * Based on Figma design with back button, logo, title and subtitle
 */
const AuthHeader: React.FC<AuthHeaderProps> = ({
  className = '',
  children,
  title: propTitle,
  subtitle: propSubtitle
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Determine title and subtitle based on route
  const getTitle = () => {
    if (propTitle) return propTitle;
    if (location.pathname === '/login') return t('authHeader.userLogin');
    if (location.pathname === '/activate') return t('authHeader.accountActivation');
    if (location.pathname === '/forgot-password') return t('authHeader.forgotPassword');
    if (location.pathname === '/reset-password') return t('authHeader.resetPassword');
    return t('authHeader.userLogin');
  };

  const getSubtitle = () => {
    if (propSubtitle) return propSubtitle;
    if (location.pathname === '/login') return t('authHeader.verifyIdentitySubtitle');
    if (location.pathname === '/activate') return t('authHeader.activationSubtitle');
    if (location.pathname === '/forgot-password') return t('authHeader.forgotSubtitle');
    if (location.pathname === '/reset-password') return t('authHeader.resetSubtitle');
    return t('authHeader.verifyIdentitySubtitle');
  };

  const title = getTitle();
  const subtitle = getSubtitle();

  const handleBackClick = () => {
    playClickSound();
    navigate('/');
  };

  if (children) {
    return <header className={`w-full ${className}`}>{children}</header>;
  }

  const isLoginPage = location.pathname === '/login' || location.pathname === '/forgot-password';

  return (
    <header
      className={`
        relative h-full min-h-0 flex items-center
        px-3 sm:px-4 md:px-5 lg:px-8 py-1.5 sm:py-2
        w-full
        ${className}
      `}
      style={{
        width: '100%',
        borderBottom: '1px solid rgba(255, 255, 255, 0.10)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        background: 'rgba(5, 11, 24, 0.80)',
      }}
    >
      {isLoginPage ? (
        <div className="relative w-full flex items-center justify-center">
          <button
            onClick={handleBackClick}
            className="absolute left-0 top-1/2 -translate-y-1/2 flex items-center gap-1 sm:gap-1.5 text-white hover:opacity-80 transition-opacity py-1"
          >
            <ArrowLeft className="w-4 h-4 flex-shrink-0" />
            <span className="hidden sm:inline-block text-[10px] font-black uppercase tracking-wide">
              {t('authHeader.backToHome')}
            </span>
          </button>

          <div className="hidden md:flex flex-col items-center justify-center gap-0.5 pointer-events-none">
            <h1 className="text-white text-xl sm:text-2xl lg:text-3xl font-black italic tracking-wide text-center">
              {title}
            </h1>
            <div className="h-0.5 w-10 sm:w-12 lg:w-14 bg-gradient-to-r from-cyan-400 via-purple-500 to-pink-500 rounded-full" />
            <p className="text-white/60 text-[11px] sm:text-xs font-semibold tracking-wider text-center max-w-[200px] sm:max-w-none truncate">
              {subtitle}
            </p>
          </div>
        </div>
      ) : (
        <div className="w-full flex flex-row items-center justify-between gap-2">
          <button
            onClick={handleBackClick}
            className="flex items-center gap-1 sm:gap-1.5 text-white hover:opacity-80 transition-opacity py-1 flex-shrink-0"
          >
            <ArrowLeft className="w-4 h-4 flex-shrink-0" />
            <span className="text-[10px] sm:text-xs font-black uppercase tracking-wide">
              {t('authHeader.backToHome')}
            </span>
          </button>

          <div className="hidden md:flex flex-1 flex-col items-center justify-center gap-0.5 min-w-0">
            <h1 className="text-white text-lg sm:text-xl lg:text-2xl font-black italic tracking-wide text-center truncate w-full">
              {title}
            </h1>
            <div className="h-0.5 w-10 sm:w-12 lg:w-14 bg-gradient-to-r from-cyan-400 via-purple-500 to-pink-500 rounded-full" />
            <p className="text-white/60 text-[9px] sm:text-[10px] font-semibold tracking-wider text-center truncate w-full">
              {subtitle}
            </p>
          </div>

          <div className="w-4 flex-shrink-0 sm:w-20" aria-hidden />
        </div>
      )}
    </header>
  );
};

export default AuthHeader;
